import { ReactComponent as plusIcon } from '@epam/assets/icons/common/action-add-12.svg';
import { Button, FlexCell, FlexRow, TabButton } from '@epam/promo';
import { useEffect, useMemo, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import { CardList } from 'components/broadcasts/card-list';
import { BlankBlock, FeatureGate } from 'components/common';
import CardFilters from 'components/common/CardFilters/CardFilters';
import { useBroadcastSwitcher } from 'hooks';
import { isBroadcastReadonly, useAppLocation } from 'hooks/helpers';
import { Broadcast } from 'models/broadcast.model';
import { broadcastRoutes } from 'router/broadcastRoutes';
import {
  TAB_STATUSES,
  BROADCAST_TABS,
  resetDraft,
  useBroadcastStore,
} from 'store/broadcast';
import { useFavoritesStore } from 'store/favorites';
import { useFeatureTogglesStore } from 'store/feature-toggling';
import css from './BroadcastPage.module.scss';
import { BroadcastCommentsColumn } from 'components/broadcasts/Comments';
import { BroadcastPreview } from 'components/common/PreviewWrapper';
import { BroadcastProgress } from 'components/broadcasts/BroadcastProgress';

export const BroadcastPageView = () => {
  const navigate = useNavigate();
  const switchBroadcast = useBroadcastSwitcher();
  const {
    draft,
    inProgressBroadcasts,
    publishedBroadcasts,
    activeTab,
    activeBroadcastTab,
    actions: { setActiveTab, setActiveBroadcastTab },
  } = useBroadcastStore((state) => state);
  const { isCreate, isNotFound } = useAppLocation(broadcastRoutes);
  const panelRef = useRef<HTMLDivElement>(null);
  const favoriteBroadcastIds = useFavoritesStore(
    (state) => state.favoriteBroadcastIds,
  );
  const { isFeatureEnabled } = useFeatureTogglesStore();
  const isCommentsEnabled = isFeatureEnabled('broadcastComment');

  const isReadonly = useMemo(
    () => isBroadcastReadonly(draft.status),
    [draft.status],
  );

  useEffect(() => {
    const isBroadcastTabPreviewOrEmpty =
      activeBroadcastTab === BROADCAST_TABS.PREVIEW || !activeBroadcastTab;
    const isBroadcastTabProgressOrEmpty =
      activeBroadcastTab === BROADCAST_TABS.PROGRESS || !activeBroadcastTab;

    if (isReadonly && isBroadcastTabPreviewOrEmpty) {
      setActiveBroadcastTab(BROADCAST_TABS.PROGRESS);
    }

    if (!isReadonly && isBroadcastTabProgressOrEmpty) {
      setActiveBroadcastTab(BROADCAST_TABS.PREVIEW);
    }
  }, [isReadonly, activeBroadcastTab, setActiveBroadcastTab]);

  const activeRightTab = useMemo(() => {
    const isCommentsTabWithoutFeature =
      activeBroadcastTab === BROADCAST_TABS.COMMENTS && !isCommentsEnabled;
    const isPublishedOnPreviewTab =
      isReadonly && activeBroadcastTab === BROADCAST_TABS.PREVIEW;
    const isInProgressOnProgressTab =
      !isReadonly && activeBroadcastTab === BROADCAST_TABS.PROGRESS;

    if (isCommentsTabWithoutFeature) {
      return isReadonly ? BROADCAST_TABS.PROGRESS : BROADCAST_TABS.PREVIEW;
    }

    if (isPublishedOnPreviewTab) {
      return BROADCAST_TABS.PROGRESS;
    }

    if (isInProgressOnProgressTab) {
      return BROADCAST_TABS.PREVIEW;
    }

    return (
      activeBroadcastTab ||
      (isReadonly ? BROADCAST_TABS.PROGRESS : BROADCAST_TABS.PREVIEW)
    );
  }, [activeBroadcastTab, isReadonly, isCommentsEnabled]);

  const activeBroadcasts = useMemo(
    () =>
      activeTab === TAB_STATUSES.IN_PROGRESS
        ? inProgressBroadcasts
        : publishedBroadcasts,
    [activeTab, inProgressBroadcasts, publishedBroadcasts],
  );
  const items = isCreate ? [draft, ...activeBroadcasts] : [...activeBroadcasts];
  const lastInProgressBroadcastId = useRef<string>(null);
  const lastPublishedBroadcastId = useRef<string>(null);

  const showBroadcastData = () => {
    if (isNotFound) {
      return (
        <BlankBlock
          header="Broadcast is not found!"
          message="Please create another broadcast"
        />
      );
    }

    if (activeBroadcasts?.length || isCreate) {
      return <Outlet />;
    }

    return (
      <BlankBlock
        header="Nothing to display yet"
        message="Please create your first broadcast"
      />
    );
  };

  const navigateToTheFirstBroadcast = (
    list: Broadcast[],
    lastSavedId: string | null,
  ) => {
    if (!list.length) {
      return navigate(broadcastRoutes.notFound);
    }

    const newDraftbroadcast = lastSavedId
      ? list.find((broadcast) => broadcast.id === lastSavedId)
      : list[0];

    const route = newDraftbroadcast
      ? broadcastRoutes.broadcastId(newDraftbroadcast.id)
      : broadcastRoutes.notFound;

    return navigate(route);
  };

  const onInProgressTab = () => {
    if (activeTab === TAB_STATUSES.IN_PROGRESS) return;

    setActiveTab(TAB_STATUSES.IN_PROGRESS);
    setActiveBroadcastTab(BROADCAST_TABS.PREVIEW); // Reset to Preview for In Progress
    lastPublishedBroadcastId.current = draft.id;

    return navigateToTheFirstBroadcast(
      inProgressBroadcasts,
      lastInProgressBroadcastId.current,
    );
  };

  const handleSwitchToPublishedTab = () => {
    resetDraft();
    setActiveTab(TAB_STATUSES.PUBLISHED);
    setActiveBroadcastTab(BROADCAST_TABS.PROGRESS); // Reset to Progress for Published
    lastInProgressBroadcastId.current = draft.id;

    return navigateToTheFirstBroadcast(
      publishedBroadcasts,
      lastPublishedBroadcastId.current,
    );
  };

  const onPublishedTab = () => {
    if (activeTab === TAB_STATUSES.PUBLISHED) return;

    switchBroadcast({
      onSuccess: handleSwitchToPublishedTab,
      onAbort: handleSwitchToPublishedTab,
    });
  };

  return (
    <FlexRow cx={css.page__container}>
      <FlexCell ref={panelRef} cx={css['page__left-panel']} grow={1}>
        <Button
          caption="Add a new broadcast"
          color="blue"
          cx={css.page__btn}
          icon={plusIcon}
          rawProps={{ 'data-testid': 'add-broadcast-btn' }}
          onClick={() => {
            switchBroadcast({
              onSuccess: () => {
                navigate(broadcastRoutes.create);
                resetDraft();
                setActiveTab(TAB_STATUSES.IN_PROGRESS);
                setActiveBroadcastTab(BROADCAST_TABS.PREVIEW); // Reset to Preview for new broadcast
              },
              onAbort: () => {
                navigate(broadcastRoutes.create);
                resetDraft();
                setActiveTab(TAB_STATUSES.IN_PROGRESS);
                setActiveBroadcastTab(BROADCAST_TABS.PREVIEW); // Reset to Preview for new broadcast
              },
            });
          }}
        />
        <FlexCell>
          <FlexRow borderBottom>
            <TabButton
              caption={TAB_STATUSES.IN_PROGRESS}
              cx={css['page__tab-btn']}
              isActive={activeTab === TAB_STATUSES.IN_PROGRESS}
              size="36"
              onClick={onInProgressTab}
            />
            <TabButton
              caption={TAB_STATUSES.PUBLISHED}
              cx={css['page__tab-btn']}
              isActive={activeTab === TAB_STATUSES.PUBLISHED}
              size="36"
              onClick={onPublishedTab}
            />
          </FlexRow>
        </FlexCell>
        {activeBroadcasts?.length && (
          <CardFilters
            key={activeTab}
            CardList={CardList}
            favoritesFilter={{
              isFavorite: (item) => !!favoriteBroadcastIds[item.id],
            }}
            isFilterVisible={activeTab !== TAB_STATUSES.PUBLISHED}
            items={items}
            panelHeight={
              panelRef.current ? panelRef.current.clientHeight - 60 : 0
            }>
            <CardList items={items} />
          </CardFilters>
        )}
      </FlexCell>
      <FlexCell grow={2.5}>{showBroadcastData()}</FlexCell>
      <FlexCell cx={css['page__right-panel']} grow={1}>
        {/* Show tabs for published broadcasts (Progress/Comments) */}
        {isReadonly && isCommentsEnabled && (
          <FlexRow borderBottom cx={css.page__tabs}>
            <TabButton
              caption="Progress"
              cx={css['page__tab-btn']}
              isActive={activeRightTab === BROADCAST_TABS.PROGRESS}
              rawProps={{ 'data-testid': 'broadcast-tab-progress' }}
              size="36"
              onClick={() => setActiveBroadcastTab(BROADCAST_TABS.PROGRESS)}
            />
            <TabButton
              caption="Comments"
              cx={css['page__tab-btn']}
              isActive={activeRightTab === BROADCAST_TABS.COMMENTS}
              rawProps={{ 'data-testid': 'broadcast-tab-comments' }}
              size="36"
              onClick={() => setActiveBroadcastTab(BROADCAST_TABS.COMMENTS)}
            />
          </FlexRow>
        )}
        {/* Show tabs for in-progress broadcasts (Preview/Comments) */}
        {!isReadonly && isCommentsEnabled && (
          <FlexRow borderBottom cx={css.page__tabs}>
            <TabButton
              caption="Preview"
              cx={css['page__tab-btn']}
              isActive={activeRightTab === BROADCAST_TABS.PREVIEW}
              rawProps={{ 'data-testid': 'broadcast-tab-preview' }}
              size="36"
              onClick={() => setActiveBroadcastTab(BROADCAST_TABS.PREVIEW)}
            />
            <TabButton
              caption="Comments"
              cx={css['page__tab-btn']}
              isActive={activeRightTab === BROADCAST_TABS.COMMENTS}
              rawProps={{ 'data-testid': 'broadcast-tab-comments' }}
              size="36"
              onClick={() => setActiveBroadcastTab(BROADCAST_TABS.COMMENTS)}
            />
          </FlexRow>
        )}
        {/* Render content based on current tab */}
        {activeRightTab === BROADCAST_TABS.PREVIEW && !isReadonly && (
          <BroadcastPreview key={draft.id} />
        )}
        {activeRightTab === BROADCAST_TABS.COMMENTS && (
          <FeatureGate feature="broadcastComment">
            <BroadcastCommentsColumn />
          </FeatureGate>
        )}
        {activeRightTab === BROADCAST_TABS.PROGRESS && isReadonly && (
          <BroadcastProgress />
        )}
      </FlexCell>
    </FlexRow>
  );
};
